import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Promise-based confirm/prompt dialogs — the enterprise replacement for
 * window.confirm / window.prompt. Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, description, confirmText, danger }))) return;
 *   const value = await confirm({ title, prompt: { placeholder, type } }); // string | null
 * Also exposed imperatively as `confirmDialog(...)` for non-component code.
 */
const ConfirmCtx = createContext(null);
let _imperative = null;
export const confirmDialog = (opts) => (_imperative ? _imperative(opts) : Promise.resolve(window.confirm(opts?.title || "Are you sure?")));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {opts, resolve}
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const resolveRef = useRef(null);

  const open = useCallback((opts) => new Promise((resolve) => {
    resolveRef.current = resolve;
    setValue(opts?.prompt?.defaultValue || "");
    setState({ opts: opts || {} });
  }), []);
  _imperative = open;

  const close = (result) => {
    const r = resolveRef.current; resolveRef.current = null;
    setState(null); setBusy(false);
    r?.(result);
  };

  const o = state?.opts || {};
  const isPrompt = !!o.prompt;
  const onConfirm = () => {
    if (isPrompt) {
      if (o.prompt.minLength && value.length < o.prompt.minLength) return;
      close(value);
    } else close(true);
  };

  return (
    <ConfirmCtx.Provider value={open}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && close(isPrompt ? null : false)}
          role="dialog" aria-modal="true" data-testid="confirm-dialog">
          <div className="w-full max-w-md rounded-3xl bg-[#FDFBF7] border border-stone-200 shadow-xl p-6">
            <div className="flex items-start gap-3">
              {o.danger && <span className="shrink-0 inline-flex p-2 rounded-xl bg-red-50 text-red-600"><AlertTriangle size={18} /></span>}
              <div className="min-w-0">
                <h2 className="font-display text-xl text-stone-900">{o.title || "Are you sure?"}</h2>
                {o.description && <p className="text-sm text-stone-600 mt-1.5 leading-relaxed whitespace-pre-line">{o.description}</p>}
              </div>
            </div>
            {isPrompt && (
              <input autoFocus type={o.prompt.type || "text"} value={value} onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onConfirm()} placeholder={o.prompt.placeholder || ""}
                data-testid="confirm-input"
                className="mt-4 w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:border-stone-900" />
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => close(isPrompt ? null : false)} data-testid="confirm-cancel"
                className="px-4 py-2 rounded-full border border-stone-300 text-sm text-stone-700 hover:border-stone-900">{o.cancelText || "Cancel"}</button>
              <button onClick={onConfirm} disabled={busy || (isPrompt && o.prompt.minLength && value.length < o.prompt.minLength)} data-testid="confirm-ok"
                className={`px-4 py-2 rounded-full text-sm font-medium text-white disabled:opacity-50 ${o.danger ? "bg-red-600 hover:bg-red-700" : "bg-stone-900 hover:bg-[#C2410C]"}`}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : (o.confirmText || (isPrompt ? "Save" : "Confirm"))}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmCtx) || confirmDialog;
